/**
 * One enrolment attempt, step by step (spec section 6.7 "Enrolment is silent
 * and resumable"; decision 40). `enrolment.js` decides WHICH step runs and
 * what the answer means for the caller; this module is the steps.
 *
 *   prepareAttempt         generate MK, the device pair and the wrapped
 *                          signing key; write the row, `prepared`
 *   proveProvisionalCopy   write the provisional platform slot and read it back
 *   markSent               the row goes `ambiguous`, durably, BEFORE the POST
 *   postAttempt            the identical request, every time
 *   mintFreshDeviceId      DEVICE_ID_IN_USE: a new id into the durable row
 *   reconcileAttempt       one status read, made directly, then `verdictOf`
 *   verdictOf              the section 5.2 table: what a status body says
 *                          about this attempt
 *   finaliseAttempt        ONE store transaction, then publish, then the
 *                          committed slot, then neutralise the provisional one
 *   discardAttempt         zero, neutralise, delete — complete or not at all
 *
 * THE MATCH IS ON `keyId` + `signingKey`, NEVER ON `thisDevice`. The device
 * view is resolved from the `X-Device-Id` header, and losing that header on
 * the reconciling read would strand the member on an approval screen with no
 * other device to approve from. `keyId` (122 random bits) and `signingKey` (a
 * P-256 point) are in the body and cannot be stripped by a proxy. `thisDevice`
 * only refines a match: absent means the install registers itself after
 * finalising; `REVOKED` means a holder of MK has disowned this install, and
 * the attempt is discarded rather than promoted (decision 18).
 */
import { ATTEMPT_STATE } from '@/utils/courses-crypto/key-store';
import { DEVICE_STATUSES } from '@/utils/courses-encryption-api';
import {
  PLATFORM_COPY_SLOT,
  neutraliseSlot,
  readSlot,
  slotHolds,
  writeSlot,
} from './platform-slots';

/** What a status body says about an attempt. */
export const VERDICT = Object.freeze({
  /** The server holds this attempt's bundle. */
  COMMITTED: 'committed',
  /** No bundle, as far as this read can see. A re-send is the remedy. */
  NOT_COMMITTED: 'not-committed',
  /** A bundle exists and it is not this attempt's: genuinely enrolled elsewhere. */
  SUPERSEDED: 'superseded',
  /** This attempt's bundle, and this install revoked from it by a holder of MK. */
  REVOKED: 'revoked',
  /** The read did not land, or the body is not a status. Still ambiguous. */
  UNKNOWN: 'unknown',
});

/**
 * @param {unknown} bytes
 */
export function zeroBytes(bytes) {
  if (bytes && typeof bytes.fill === 'function') bytes.fill(0);
}

/**
 * Generate everything one enrolment needs and write it as a `prepared`
 * attempt. `generateMasterKey` runs here and nowhere else: while a row
 * exists it is never called again for this member.
 *
 * A device that already registered keeps its identity: the API never upgrades
 * a known device id, so minting a second one would leave a stale row behind.
 *
 * @param {{ store: object, deps: object, tools: object, memberId: number }} args
 * @returns {Promise<import('@/utils/courses-crypto/key-store').AttemptRecord>}
 * @throws when the row could not be written; MK has been zeroed by then
 */
export async function prepareAttempt({ store, deps, tools, memberId }) {
  const { keyId, mk } = tools.generateMasterKey();
  const identity = store.getters['courses-keys/deviceIdentity'];
  const reuse = identity?.deviceId && identity?.devicePublicKey && identity?.devicePrivateKey;
  const deviceId = reuse ? identity.deviceId : deps.newDeviceId();
  const pair = reuse
    ? { publicKey: identity.devicePublicKey, privateKey: identity.devicePrivateKey }
    : await tools.generateDeviceKeyPair();
  const signing = await tools.createWrappedSigningKey({ mk, keyId, memberId });
  const attempt = {
    memberId,
    state: ATTEMPT_STATE.PREPARED,
    keyId,
    mk,
    deviceId,
    devicePublicKey: pair.publicKey,
    devicePrivateKey: pair.privateKey,
    signingKey: signing.publicKey,
    signingKeyWrapped: signing.wrapping,
    deviceIdRetries: 0,
    preparedAt: deps.now(),
    postedAt: null,
    rejectedAt: null,
  };
  try {
    await store.dispatch('courses-keys/prepareAttempt', attempt);
  } catch (error) {
    // Nothing recorded, nothing sent: drop the key we made.
    zeroBytes(mk);
    throw error;
  }
  return attempt;
}

/**
 * The provisional platform copy, written and read back (decision 40). Before
 * the POST, where a failure still changes the outcome: nothing has been
 * created, so aborting costs the member nothing.
 *
 * @param {{ tools: object, memberId: number, attempt: object }} args
 * @returns {Promise<boolean>} whether the slot holds the copy
 * @throws whatever the adapter throws
 */
export function proveProvisionalCopy({ tools, memberId, attempt }) {
  return writeSlot({
    adapter: tools.resolveAdapter(),
    memberId,
    keyId: attempt.keyId,
    mk: attempt.mk,
    slot: PLATFORM_COPY_SLOT.PROVISIONAL,
  });
}

/**
 * Records, durably, that the attempt is being sent. `prepared → ambiguous`
 * is the only edge that sets `ambiguous`, and there is no edge back: from
 * here every answer is reconciled before it is interpreted.
 *
 * @param {{ store: object, deps: object, attempt: object }} args
 * @returns {Promise<object>} the row as stored
 */
export async function markSent({ store, deps, attempt }) {
  if (attempt.state === ATTEMPT_STATE.AMBIGUOUS) return attempt;
  return store.dispatch('courses-keys/updateAttempt', {
    state: ATTEMPT_STATE.AMBIGUOUS,
    postedAt: deps.now(),
  });
}

/**
 * What this install registers as: the attempt's identity with the platform's
 * label and kind, described afresh — they are not part of the replay match,
 * and the API keeps the ones it first saw.
 *
 * @param {{ deps: object, attempt: object }} args
 * @returns {Promise<{ id: string, publicKey: string, name: string, platform: string }>}
 */
async function deviceOf({ deps, attempt }) {
  const { platform, name } = await deps.describeThisDevice();
  return { id: attempt.deviceId, publicKey: attempt.devicePublicKey, name, platform };
}

/**
 * The stored attempt, on the wire.
 *
 * @param {{ token: string | null, deps: object, attempt: object }} args
 * @returns {Promise<object>} the 201 body — the full EncryptionStatus
 */
export async function postAttempt({ token, deps, attempt }) {
  return deps.enrol(
    { token, deviceId: attempt.deviceId },
    {
      keyId: attempt.keyId,
      signingKey: attempt.signingKey,
      signingKeyWrapped: attempt.signingKeyWrapped,
      device: await deviceOf({ deps, attempt }),
    },
  );
}

/**
 * DEVICE_ID_IN_USE: the id is taken and no bundle exists, so the one thing
 * that can succeed is a fresh device id. Into the durable row, so a restart
 * re-sends the same fresh attempt rather than minting a third.
 *
 * @param {{ store: object, deps: object, tools: object, attempt: object }} args
 * @returns {Promise<object>} the row as stored
 */
export async function mintFreshDeviceId({ store, deps, tools, attempt }) {
  const pair = await tools.generateDeviceKeyPair();
  return store.dispatch('courses-keys/updateAttempt', {
    deviceId: deps.newDeviceId(),
    devicePublicKey: pair.publicKey,
    devicePrivateKey: pair.privateKey,
    deviceIdRetries: (attempt.deviceIdRetries ?? 0) + 1,
  });
}

/**
 * The section 5.2 table, on a body of the GET shape — a status read, or a
 * 201, whose body is by contract the GET body for the same `X-Device-Id`.
 *
 * @param {unknown} status
 * @param {{ keyId: string, signingKey: string }} attempt
 * @returns {{ kind: string, status?: object, registered?: boolean }}
 */
export function verdictOf(status, attempt) {
  if (!status || typeof status !== 'object') return { kind: VERDICT.UNKNOWN };
  if (status.enrolled !== true) return { kind: VERDICT.NOT_COMMITTED, status };
  // SUPERSEDED is the one verdict that destroys key material, so it is never
  // reached on a body the contract does not describe: an enrolled status
  // carries both fields as strings, and one that does not is not a status.
  if (typeof status.keyId !== 'string' || typeof status.signingKey !== 'string') {
    return {
      kind: VERDICT.UNKNOWN,
      error: new Error('The status body is not an EncryptionStatus'),
    };
  }
  const mine = status.keyId === attempt.keyId && status.signingKey === attempt.signingKey;
  if (!mine) return { kind: VERDICT.SUPERSEDED, status };
  const thisDevice = status.thisDevice ?? null;
  if (thisDevice?.status === DEVICE_STATUSES.REVOKED) return { kind: VERDICT.REVOKED, status };
  // Only an APPROVED row counts as registered; absent or PENDING, the install
  // registers itself through the signed self-approval path after finalising.
  return {
    kind: VERDICT.COMMITTED,
    status,
    registered: thisDevice?.status === DEVICE_STATUSES.APPROVED,
  };
}

/**
 * One status read, MADE DIRECTLY AGAINST THE CLIENT, not through the store.
 * `courses-devices/refreshStatus` builds its auth from a local device identity
 * that does not exist yet mid-attempt (so `thisDevice` would come back null),
 * and it commits the snapshot — which would tell the store `enrolled: true`
 * before this device's key is live, the window this whole design closes.
 *
 * @param {{ token: string | null, deps: object, attempt: object }} args
 * @returns {Promise<{ kind: string, status?: object, registered?: boolean, error?: unknown }>}
 */
export async function reconcileAttempt({ token, deps, attempt }) {
  let status;
  try {
    status = await deps.fetchEncryptionStatus({ token, deviceId: attempt.deviceId });
  } catch (error) {
    return { kind: VERDICT.UNKNOWN, error };
  }
  return verdictOf(status, attempt);
}

/**
 * The server holds this attempt's bundle: make it this device's key.
 *
 * ORDER, and each step is load-bearing:
 *
 *   1. ONE store transaction: the key record appears and the attempt row goes.
 *      The bytes were already on disk, so there is no instant at which the
 *      server holds K and no durable MK exists.
 *   2. Publish: only now does the store learn `enrolled: true` — after this
 *      device's key is live, never before.
 *   3. The committed platform slot, written and read back. AFTER the
 *      promotion, not before: once the server holds the bundle a platform
 *      failure cannot un-create it, and withholding MK from the device that
 *      holds it would manufacture the unrecoverable state this exists to
 *      prevent. So a failure here is reported and retried every session
 *      (`enrolment.js`, the once-per-session repair), and the provisional
 *      slot is KEPT until the committed one verifies, so the key stays on the
 *      platform-carried medium throughout.
 *   4. Neutralise the provisional slot.
 *
 * @param {{ store: object, deps: object, tools: object, memberId: number,
 *   attempt: object, status: object, registered: boolean }} args
 * @returns {Promise<{ published: boolean, platformCopyWritten: boolean }>}
 *   `published` false means the key is live and the store's status was not
 *   told — which hydration and the next status read repair on their own
 * @throws ONLY when the promotion itself failed; the attempt row is still there
 */
export async function finaliseAttempt({
  store,
  deps,
  tools,
  memberId,
  attempt,
  status,
  registered,
}) {
  await store.dispatch('courses-keys/promoteAttempt', { attempt, needsRegistration: !registered });

  // From here the key is live and nothing below may take that back.
  let published = true;
  try {
    store.commit('courses-keys/setEnrolment', {
      status: tools.ENROLMENT_STATUS.ENROLLED,
      hasRecoveryCode: status.hasRecoveryCode === true,
    });
    await store.dispatch('courses-devices/setLocalDevice', await deviceOf({ deps, attempt }));
    store.commit('courses-devices/setStatus', status);
  } catch (error) {
    console.warn('[CoursesE2EE] The key is live but the store was not told:', error);
    published = false;
  }

  const platformCopyWritten = await ensureCommittedCopy({
    tools,
    memberId,
    keyId: attempt.keyId,
    mk: attempt.mk,
  });
  return { published, platformCopyWritten };
}

/**
 * The committed slot holds this key, or is written until it does; then the
 * provisional slot is cleared. Shared by the finalisation above and by the
 * once-per-session repair, which is how a copy the finalisation could not
 * write — and every member #1577 enrolled with the copy write swallowed —
 * gets one. Never throws: the answer is the boolean.
 *
 * @param {{ tools: object, memberId: number, keyId: string, mk: Uint8Array }} args
 * @returns {Promise<boolean>} whether the committed slot holds the key now
 */
export async function ensureCommittedCopy({ tools, memberId, keyId, mk }) {
  const adapter = tools.resolveAdapter();
  const committed = PLATFORM_COPY_SLOT.COMMITTED;
  let holds = false;
  try {
    holds =
      (await slotHolds({ adapter, memberId, keyId, mk, slot: committed })) ||
      (await writeSlot({ adapter, memberId, keyId, mk, slot: committed }));
  } catch (error) {
    console.warn('[CoursesE2EE] The platform copy of the key was not written:', error);
  }
  if (!holds) {
    console.warn(
      '[CoursesE2EE] The provisional platform copy is kept until the committed one verifies',
    );
    return false;
  }
  try {
    const provisional = await readSlot({ adapter, memberId, slot: PLATFORM_COPY_SLOT.PROVISIONAL });
    if (provisional) {
      zeroBytes(provisional.mk);
      await neutraliseSlot({ adapter, memberId, slot: PLATFORM_COPY_SLOT.PROVISIONAL });
    }
  } catch (error) {
    // Harmless: two slots, same key. Hydration reads only the committed one.
    console.warn('[CoursesE2EE] The provisional platform copy was not cleared:', error);
  }
  return true;
}

/**
 * Discarding is the only operation on this path that destroys key material,
 * so it happens only on proof (section 6.7: a status read showing a
 * different bundle, or a revoke), and when it happens it is complete or not
 * at all: the bytes zeroed, the provisional slot neutralised, and ONLY THEN
 * the row deleted. The slot before the row: an overwrite that lands makes
 * the slot yield nothing even if its removal fails, and on iOS that slot is
 * synced. An overwrite that fails keeps the row, so the next reconcile
 * reaches the same verdict and tries the slot again — a row-less copy of a
 * revoked install's key would otherwise be adopted as the member's own on
 * the next open, which is the revoke bypass decision 18 forbids.
 *
 * @param {{ store: object, tools: object, memberId: number, attempt: object }} args
 * @returns {Promise<{ neutralised: boolean, discarded: boolean }>}
 */
export async function discardAttempt({ store, tools, memberId, attempt }) {
  zeroBytes(attempt.mk);
  const neutralised = await neutraliseSlot({
    adapter: tools.resolveAdapter(),
    memberId,
    slot: PLATFORM_COPY_SLOT.PROVISIONAL,
  });
  if (!neutralised) {
    console.warn('[CoursesE2EE] The attempt is kept until its platform copy can be cleared');
    return { neutralised, discarded: false };
  }
  await store.dispatch('courses-keys/discardAttempt');
  return { neutralised, discarded: true };
}

/**
 * Section 5.3's recovery route: no key record, a live bundle on the server,
 * and a provisional copy for exactly that bundle's `keyId` — a member who
 * signed out mid-attempt, whose IndexedDB was evicted between the commit and
 * the promotion, or a sibling device that finds the synced copy after the
 * enrolment did commit. Adopted exactly as hydration adopts a restored
 * phone's copy, and from there the ordinary self-registration takes over.
 *
 * The guard is the status read: a provisional copy is adopted ONLY for a
 * bundle that demonstrably exists under its own `keyId`. And the read is
 * made only when there is a copy to adopt — the slot is looked at first, so
 * a member with nothing in it costs no request.
 *
 * @param {{ store: object, tools: object, memberId: number }} args
 * @returns {Promise<boolean>} whether a copy was adopted
 */
export async function adoptProvisionalCopy({ store, tools, memberId }) {
  let copy;
  try {
    copy = await readSlot({
      adapter: tools.resolveAdapter(),
      memberId,
      slot: PLATFORM_COPY_SLOT.PROVISIONAL,
    });
  } catch (error) {
    console.warn('[CoursesE2EE] The provisional platform copy could not be read:', error);
    return false;
  }
  if (!copy) return false;
  let status;
  try {
    status = await store.dispatch('courses-devices/refreshStatus');
  } catch (error) {
    console.warn('[CoursesE2EE] The status read behind the provisional copy did not land:', error);
    return false;
  }
  if (status?.enrolled !== true || copy.keyId !== status.keyId) {
    zeroBytes(copy.mk);
    return false;
  }
  await store.dispatch('courses-keys/adoptCopy', copy);
  return true;
}

/**
 * Whether the provisional slot holds a copy, without adopting it: the
 * stranded-state diagnostic (#1652). The bytes are forgotten at once.
 *
 * @param {{ tools: object, memberId: number }} args
 * @returns {Promise<boolean|null>} null when the platform could not be asked
 */
export async function peekProvisionalCopy({ tools, memberId }) {
  try {
    const copy = await readSlot({
      adapter: tools.resolveAdapter(),
      memberId,
      slot: PLATFORM_COPY_SLOT.PROVISIONAL,
    });
    if (copy?.mk) zeroBytes(copy.mk);
    return !!copy;
  } catch (error) {
    console.warn('[CoursesE2EE] The provisional platform copy could not be read:', error);
    return null;
  }
}
